/**
 * Dynamic Pricing Engine — shared resolution logic.
 *
 * Supports three pricing modes:
 *   - fixed:    no dynamic modifiers applied
 *   - safe:     predefined demand ladder with ±25% cap (default)
 *   - advanced: full rule engine with ±40% cap (requires admin approval)
 *
 * Used by both frontend (card-level & slot-level pricing) and edge functions
 * (authoritative server-side calculation).
 */

export type PricingRuleType =
  | "seasonal"
  | "demand"
  | "early_bird"
  | "day_of_week"
  | "demand_ladder"
  | "velocity"
  | "city_demand"
  | "home_demand"
  | "last_minute_premium";

export type PricingMode = "fixed" | "safe" | "advanced";

export type ResolutionMode = "additive" | "grouped" | "highest_wins";

export interface PricingRule {
  id: string;
  rule_type: PricingRuleType;
  modifier_percent: number;
  issuer_type: "admin" | "provider";
  provider_id: string | null;
  experience_id: string | null;
  config: Record<string, unknown>;
  is_active: boolean;
  priority: number;
  resolution_mode?: ResolutionMode;
}

export interface SlotContext {
  date: string; // YYYY-MM-DD
  capacity_remaining?: number;
  capacity_total?: number;
  bookings_last_24h?: number; // for velocity calculation
}

export interface ExperienceContext {
  id: string;
  provider_id: string;
  season: string;
  pricing_mode?: PricingMode;
  floor_price_gross?: number | null;
  base_price_gross?: number | null;
  pricing_advanced_approved?: boolean;
}

export interface DemandContext {
  city_demand_index?: number; // from boom_city_demand_daily
  home_demand_index?: number; // from boom_listing_demand_daily
}

export interface AppliedModifier {
  rule_type: PricingRuleType | "safe_demand" | "safe_velocity" | "safe_time" | "safe_dow" | "safe_city";
  modifier_percent: number;
  label: string;
  group?: string; // for grouped resolution
}

// --- Caps ---
const ADVANCED_MAX_MODIFIER = 40;
const ADVANCED_MIN_MODIFIER = -40;
const SAFE_MAX_MODIFIER = 25;
const SAFE_MIN_MODIFIER = -25;

// ============================================================
// SAFE MODE — predefined demand ladder (no configurable rules)
// ============================================================

export interface SafeModeResult {
  modifiers: AppliedModifier[];
  totalModifier: number;
}

/**
 * Resolve Safe Mode modifiers using fixed business rules.
 * Groups: DemandGroup (max of ladder + velocity + citySoft),
 *         TimeGroup (early OR last-minute, not both),
 *         DOWGroup (weekend).
 * Cap: ±25%.
 */
export function resolveSafeModifiers(
  slot: SlotContext,
  _experience: ExperienceContext,
  demand?: DemandContext
): SafeModeResult {
  const modifiers: AppliedModifier[] = [];

  // --- DemandGroup: take the max of ladder, velocity, citySoft ---
  const demandCandidates: AppliedModifier[] = [];

  // 1. Demand ladder (fill rate)
  if (slot.capacity_total != null && slot.capacity_remaining != null && slot.capacity_total > 0) {
    const fillRate = ((slot.capacity_total - slot.capacity_remaining) / slot.capacity_total) * 100;
    let ladderMod = 0;
    let label = "";
    if (fillRate >= 80) {
      ladderMod = 15;
      label = "High demand +15%";
    } else if (fillRate >= 60) {
      ladderMod = 10;
      label = "High demand +10%";
    } else if (fillRate >= 30) {
      ladderMod = 5;
      label = "Moderate demand +5%";
    }
    if (ladderMod > 0) {
      demandCandidates.push({
        rule_type: "safe_demand",
        modifier_percent: ladderMod,
        label,
        group: "demand",
      });
    }
  }

  // 2. Velocity: 3+ bookings in last 24h → +5%
  if (slot.bookings_last_24h != null && slot.bookings_last_24h >= 3) {
    demandCandidates.push({
      rule_type: "safe_velocity",
      modifier_percent: 5,
      label: "Trending +5%",
      group: "demand",
    });
  }

  // 3. CityDemandIndex soft: ≥1.5 → +3% (invisible to providers in display)
  if (demand?.city_demand_index != null && demand.city_demand_index >= 1.5) {
    demandCandidates.push({
      rule_type: "safe_city",
      modifier_percent: 3,
      label: "Area demand +3%",
      group: "demand",
    });
  }

  // Pick max from demand group
  if (demandCandidates.length > 0) {
    const best = demandCandidates.reduce((a, b) =>
      b.modifier_percent > a.modifier_percent ? b : a
    );
    modifiers.push(best);
  }

  // --- TimeGroup: early bird OR last-minute (not both) ---
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const slotDate = new Date(slot.date + "T00:00:00");
  const diffDays = Math.floor((slotDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays > 14) {
    modifiers.push({
      rule_type: "safe_time",
      modifier_percent: -10,
      label: "Early bird -10%",
      group: "time",
    });
  } else if (diffDays >= 0 && diffDays < 2) {
    modifiers.push({
      rule_type: "safe_time",
      modifier_percent: 10,
      label: "Last minute +10%",
      group: "time",
    });
  }

  // --- DOWGroup: weekend (Sat=6, Sun=0 in JS) ---
  const jsDay = slotDate.getDay();
  if (jsDay === 0 || jsDay === 6) {
    modifiers.push({
      rule_type: "safe_dow",
      modifier_percent: 5,
      label: "Weekend +5%",
      group: "dow",
    });
  }

  // Sum and cap at ±25%
  const rawTotal = modifiers.reduce((sum, m) => sum + m.modifier_percent, 0);
  const totalModifier = Math.max(SAFE_MIN_MODIFIER, Math.min(SAFE_MAX_MODIFIER, rawTotal));

  return { modifiers, totalModifier };
}

// ============================================================
// ADVANCED MODE — configurable rule engine (existing + extended)
// ============================================================

/**
 * Collect all matching pricing rules for a given experience + optional slot context.
 * Returns individual applied modifiers and the capped total modifier percent.
 */
export function resolveDynamicModifiers(
  rules: PricingRule[],
  experience: ExperienceContext,
  slot?: SlotContext,
  demand?: DemandContext
): { modifiers: AppliedModifier[]; totalModifier: number } {
  // 1. Filter rules that match this experience scope
  const scoped = rules.filter((r) => {
    if (!r.is_active) return false;
    if (r.experience_id === experience.id) return true;
    if (r.provider_id === experience.provider_id && !r.experience_id) return true;
    if (!r.provider_id && !r.experience_id) return true;
    return false;
  });

  // 2. Sort by priority descending
  const sorted = [...scoped].sort((a, b) => b.priority - a.priority);

  // 3. Evaluate applicability
  const applied: AppliedModifier[] = [];

  for (const rule of sorted) {
    const match = isRuleApplicable(rule, experience, slot, demand);
    if (match) {
      applied.push({
        rule_type: rule.rule_type,
        modifier_percent: rule.modifier_percent,
        label: match.label,
      });
    }
  }

  // 4. Sum and cap at ±40%
  const rawTotal = applied.reduce((sum, m) => sum + m.modifier_percent, 0);
  const totalModifier = Math.max(ADVANCED_MIN_MODIFIER, Math.min(ADVANCED_MAX_MODIFIER, rawTotal));

  return { modifiers: applied, totalModifier };
}

function isRuleApplicable(
  rule: PricingRule,
  experience: ExperienceContext,
  slot?: SlotContext,
  demand?: DemandContext
): { label: string } | null {
  const config = rule.config || {};

  switch (rule.rule_type) {
    case "seasonal": {
      const season = (config as { season?: string }).season;
      if (!season) return null;
      if (experience.season !== season) return null;
      const direction = (config as { direction?: string }).direction || "high";
      const sign = rule.modifier_percent >= 0 ? "+" : "";
      return {
        label:
          direction === "high"
            ? `High season ${sign}${rule.modifier_percent}%`
            : `Low season ${sign}${rule.modifier_percent}%`,
      };
    }

    case "demand": {
      if (!slot || slot.capacity_total == null || slot.capacity_remaining == null) return null;
      const threshold = (config as { threshold_percent?: number }).threshold_percent ?? 80;
      const fillRate = ((slot.capacity_total - slot.capacity_remaining) / slot.capacity_total) * 100;
      if (fillRate < threshold) return null;
      return { label: `High demand +${rule.modifier_percent}%` };
    }

    case "demand_ladder": {
      if (!slot || slot.capacity_total == null || slot.capacity_remaining == null) return null;
      const fillRate = ((slot.capacity_total - slot.capacity_remaining) / slot.capacity_total) * 100;
      const thresholds = (config as { thresholds?: { min: number; max: number; mod: number }[] }).thresholds ?? [];
      for (const t of thresholds) {
        if (fillRate >= t.min && fillRate < t.max) {
          const sign = t.mod >= 0 ? "+" : "";
          return { label: `Demand ${sign}${t.mod}%` };
        }
      }
      return null;
    }

    case "velocity": {
      if (!slot || slot.bookings_last_24h == null) return null;
      const minBookings = (config as { min_bookings?: number }).min_bookings ?? 3;
      if (slot.bookings_last_24h < minBookings) return null;
      const sign = rule.modifier_percent >= 0 ? "+" : "";
      return { label: `Trending ${sign}${rule.modifier_percent}%` };
    }

    case "city_demand": {
      if (!demand?.city_demand_index) return null;
      const threshold = (config as { threshold?: number }).threshold ?? 1.5;
      if (demand.city_demand_index < threshold) return null;
      const sign = rule.modifier_percent >= 0 ? "+" : "";
      return { label: `City demand ${sign}${rule.modifier_percent}%` };
    }

    case "home_demand": {
      if (!demand?.home_demand_index) return null;
      const threshold = (config as { threshold?: number }).threshold ?? 1.5;
      if (demand.home_demand_index < threshold) return null;
      const sign = rule.modifier_percent >= 0 ? "+" : "";
      return { label: `Home demand ${sign}${rule.modifier_percent}%` };
    }

    case "early_bird": {
      if (!slot) return null;
      const daysAheadMin = (config as { days_ahead_min?: number }).days_ahead_min ?? 0;
      const daysAheadMax = (config as { days_ahead_max?: number | null }).days_ahead_max;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const slotDate = new Date(slot.date + "T00:00:00");
      const diffDays = Math.floor((slotDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays < daysAheadMin) return null;
      if (daysAheadMax != null && diffDays > daysAheadMax) return null;
      const sign = rule.modifier_percent >= 0 ? "+" : "";
      if (daysAheadMin >= 14) {
        return { label: `Early bird ${sign}${rule.modifier_percent}%` };
      }
      return { label: `Last minute ${sign}${rule.modifier_percent}%` };
    }

    case "last_minute_premium": {
      if (!slot) return null;
      const hoursThreshold = (config as { hours_threshold?: number }).hours_threshold ?? 48;
      const today = new Date();
      const slotDate = new Date(slot.date + "T00:00:00");
      const diffHours = (slotDate.getTime() - today.getTime()) / (1000 * 60 * 60);
      if (diffHours > hoursThreshold || diffHours < 0) return null;
      const sign = rule.modifier_percent >= 0 ? "+" : "";
      return { label: `Last minute ${sign}${rule.modifier_percent}%` };
    }

    case "day_of_week": {
      if (!slot) return null;
      const days = (config as { days?: number[] }).days ?? [];
      const slotDate = new Date(slot.date + "T00:00:00");
      const jsDay = slotDate.getDay();
      const isoDay = jsDay === 0 ? 6 : jsDay - 1;
      if (!days.includes(isoDay)) return null;
      const sign = rule.modifier_percent >= 0 ? "+" : "";
      const isWeekend = days.includes(5) || days.includes(6);
      return {
        label: isWeekend
          ? `Weekend ${sign}${rule.modifier_percent}%`
          : `Weekday ${sign}${rule.modifier_percent}%`,
      };
    }

    default:
      return null;
  }
}

// ============================================================
// Price application helpers
// ============================================================

/**
 * Apply dynamic modifiers to a base gross price.
 */
export function applyDynamicModifier(baseGross: number, totalModifier: number): number {
  if (totalModifier === 0) return baseGross;
  return Math.ceil(baseGross * (1 + totalModifier / 100));
}

/**
 * Enforce floor price — never go below.
 */
export function enforceFloorPrice(price: number, floorPrice?: number | null): number {
  if (floorPrice == null || floorPrice <= 0) return price;
  return Math.max(price, floorPrice);
}

/**
 * Psychological rounding — round to nearest 5 or end in 9.
 * E.g. 347 → 349, 352 → 349, 498 → 499
 */
export function psychologicalRound(price: number): number {
  if (price <= 0) return price;
  // Round to nearest ending in 9 or 5
  const mod10 = price % 10;
  if (mod10 <= 2) return price - mod10 - 1; // e.g. 351 → 349
  if (mod10 <= 7) return price - mod10 + 5;  // e.g. 353 → 355 → actually let's use 9: 353→349
  return price - mod10 + 9;                   // e.g. 358 → 359
}

/**
 * Full safe-mode price resolution: apply modifiers, enforce floor, round.
 */
export function applySafeModePrice(
  baseGross: number,
  slot: SlotContext,
  experience: ExperienceContext,
  demand?: DemandContext
): { finalPrice: number; modifiers: AppliedModifier[]; totalModifier: number } {
  const { modifiers, totalModifier } = resolveSafeModifiers(slot, experience, demand);
  let price = applyDynamicModifier(baseGross, totalModifier);
  price = enforceFloorPrice(price, experience.floor_price_gross);
  price = psychologicalRound(price);
  // Final floor enforcement after rounding
  price = enforceFloorPrice(price, experience.floor_price_gross);
  return { finalPrice: price, modifiers, totalModifier };
}
